import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaMoon, FaSun } from "react-icons/fa";
import { useDarkMode } from "../components/DarkModeContext";
import { phdResearchProjects as siteProjects } from "../data/phdResearchProjects";
import {
  clearDraftFromStorage,
  loadDraftFromStorage,
  saveDraftToStorage,
} from "../utils/contentLoader";
import {
  syncProjectPeopleLinks,
  syncPillarContentLinks,
} from "../utils/relationSync";
import { isCurrentProject } from "../utils/projectDates";
import { ADMIN_IMAGES } from "../admin/media";
import AssociationEditor from "../admin/AssociationEditor";
import {
  applyDraftViaApi,
  checkContentApi,
  uploadFileViaApi,
} from "../admin/contentApi";
import {
  ChipMultiSelect,
  Field,
  FilePicker,
  ImagePicker,
  RichHint,
  StringListEditor,
  TextArea,
  TextCheck,
  TextInput,
} from "../admin/AdminFields";
import { buildSiteDraft, mergeDraftWithSite } from "../studio/buildSiteDraft";
import HomeBio from "../components/HomeBio";
import HomePitchEditor from "../admin/HomePitchEditor";

const TABS = [
  { id: "homeBio", label: "Home bio", icon: "🏠" },
  { id: "phdResearchProjects", label: "Projects", icon: "📁" },
  { id: "publications", label: "Publications / Papers", icon: "📄" },
  { id: "researchPillars", label: "Pillars", icon: "◎" },
  { id: "newsItems", label: "News", icon: "📰" },
  { id: "features", label: "Press and Social Media", icon: "✨" },
  { id: "mentoring", label: "Mentoring", icon: "🎓" },
  { id: "collaborators", label: "Collaborators", icon: "🤝" },
  { id: "awardsAndHonours", label: "Awards", icon: "🏅" },
  { id: "associationTypes", label: "Assoc. types", icon: "🔗" },
];

const reorderableTabs = new Set([
  "newsItems",
  "features",
  "mentoring",
  "collaborators",
  "awardsAndHonours",
  "phdResearchProjects",
  "publications",
  "researchPillars",
]);

const blankItem = (tab) => {
  switch (tab) {
    case "awardsAndHonours":
      return "New award";
    case "researchPillars":
      return {
        id: "new-pillar",
        title: "",
        description: "",
        bigGoal: "",
        flagshipProjectIds: [],
      };
    case "mentoring":
      return {
        id: "new-mentee",
        name: "",
        firstname: "",
        designation: "Undergraduate Student, SUTD",
        duration: "",
        projectIds: [],
      };
    case "collaborators":
      return {
        id: "new-collaborator",
        name: "",
        firstname: "",
        designation: "",
        duration: "",
        projectIds: [],
      };
    case "phdResearchProjects":
      return {
        id: "new-project",
        title: "",
        role: "Project Lead",
        duration: "",
        summary: "",
        img: "Placeholder",
        shown: true,
        contributions: [],
        publications: [],
        pillarIds: [],
        collaboratorIds: [],
        menteeIds: [],
      };
    case "publications":
      return {
        title: "",
        authors: "Kellie Yu Hui Sim",
        venue: "",
        year: new Date().getFullYear(),
        month: 1,
        tags: [],
        keywords: [],
        comments: "",
        image: "Placeholder",
        links: {},
        selected: false,
        pillarIds: [],
      };
    case "newsItems":
      return {
        date: "",
        type: "presentation",
        title: "",
        description: "",
        category: "paper",
        pillarIds: [],
        projectIds: [],
      };
    case "features":
      return { date: "", title: "", pillarIds: [], projectIds: [] };
    case "associationTypes":
      return {
        id: "new-type",
        label: "New type",
        kind: "collection",
        collection: "phdResearchProjects",
        optionId: "id",
        optionLabel: "title",
      };
    default:
      return {};
  }
};

const itemLabel = (item, index) => {
  if (typeof item === "string") return item.slice(0, 90) || `Item ${index + 1}`;
  return (
    item.title ||
    item.name ||
    item.bigGoal ||
    item.label ||
    item.id ||
    `Item ${index + 1}`
  );
};

const CardThumb = ({ imageKey }) => {
  const src = imageKey ? ADMIN_IMAGES[imageKey] : null;
  if (!src) {
    return (
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded bg-gray-100 text-[10px] text-gray-400 dark:bg-gray-800">
        —
      </div>
    );
  }
  return (
    <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
      <img src={src} alt="" className="max-h-full max-w-full object-contain" />
    </div>
  );
};

const SitePresence = ({ item, onToggleShown }) => {
  const id = item?.id;
  const siteMatch = id
    ? siteProjects.find((project) => project.id === id)
    : null;
  const isCurrent = isCurrentProject(item?.duration);
  const shown = item?.shown !== false;
  const pillarIds = item?.pillarIds || [];

  if (!id) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-700 dark:bg-gray-800 dark:text-amber-100">
        Add an ID (slug) to check where this project appears on the site.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-sm dark:border-gray-700 dark:bg-gray-900">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-semibold text-gray-800 dark:text-gray-200">
          On the website
        </p>
        {shown ? (
          <Link
            to={`/projects/${id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-green dark:text-green-dark underline underline-offset-2"
          >
            Open project page →
          </Link>
        ) : (
          <span className="font-medium text-amber-700 dark:text-amber-200">
            Hidden from public pages
          </span>
        )}
      </div>
      <div className="mt-3">
        <TextCheck
          label="Show on website"
          checked={shown}
          onChange={(v) => onToggleShown?.(v)}
        />
        <p className="mt-1 text-[11px] text-gray-500">
          Off = keep in Studio, hide from Home research, detail page, pillars,
          and home links. Save &amp; apply to update the live site data.
        </p>
      </div>
      <ul className="mt-3 space-y-1.5 text-gray-600 dark:text-gray-400">
        <li>
          <span
            className={
              siteMatch
                ? "font-medium text-green dark:text-green-dark"
                : "font-medium text-amber-700"
            }
          >
            {siteMatch
              ? "Synced into site data"
              : "Draft only — not on site yet"}
          </span>
          {!siteMatch && (
            <span> • use Save &amp; apply (or sync) to publish this ID</span>
          )}
        </li>
        <li>
          <span className="font-medium text-gray-800 dark:text-gray-200">
            Visibility:
          </span>{" "}
          {shown ? "shown" : "hidden"}
          {shown
            ? ` • Home research ${isCurrent ? "Current" : "Past"} section`
            : " • not listed publicly"}
        </li>
        <li>
          <span className="font-medium text-gray-800 dark:text-gray-200">
            Detail route:
          </span>{" "}
          <code className="text-[11px]">/projects/{id}</code>
          {!shown && " (blocked while hidden)"}
        </li>
        <li>
          <span className="font-medium text-gray-800 dark:text-gray-200">
            Pillar pages:
          </span>{" "}
          {!shown
            ? "hidden with project"
            : pillarIds.length
              ? pillarIds.map((pillarId, idx) => (
                  <span key={pillarId}>
                    {idx > 0 ? ", " : null}
                    <Link
                      to={`/home?view=agenda&pillar=${pillarId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green dark:text-green-dark underline underline-offset-2"
                    >
                      {pillarId}
                    </Link>
                  </span>
                ))
              : "none tagged yet"}
        </li>
      </ul>
    </div>
  );
};

const LivePreview = ({ tab, item }) => {
  if (item == null) {
    return <p className="text-sm text-gray-500">Select an item to preview.</p>;
  }

  if (tab === "phdResearchProjects" && typeof item === "object") {
    return (
      <article className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex aspect-[16/10] items-center justify-center bg-white p-3 dark:bg-gray-900">
          {item.img && ADMIN_IMAGES[item.img] ? (
            <img
              src={ADMIN_IMAGES[item.img]}
              alt=""
              className="max-h-36 max-w-full object-contain"
            />
          ) : null}
        </div>
        <div className="p-4">
          <h3 className="text-lg font-semibold">
            {item.title || "Untitled project"}
          </h3>
          <p className="mt-1 text-sm text-gray-500">{item.duration}</p>
          <p className="mt-2 line-clamp-4 text-sm text-gray-600 dark:text-gray-400">
            {item.summary}
          </p>
        </div>
      </article>
    );
  }

  if (tab === "publications" && typeof item === "object") {
    return (
      <article className="flex gap-3 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
        {item.image && ADMIN_IMAGES[item.image] && (
          <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded border bg-white p-1 dark:bg-gray-900">
            <img
              src={ADMIN_IMAGES[item.image]}
              alt=""
              className="max-h-full max-w-full object-contain"
            />
          </div>
        )}
        <div className="min-w-0">
          <p className="text-sm text-gray-500">
            [{item.venue} {item.year}
            {item.tags?.length ? ` • ${item.tags[0]}` : ""}]
          </p>
          <h3 className="mt-1 text-sm font-semibold leading-snug">
            {item.title || "Untitled publication"}
          </h3>
          <p className="mt-1 text-sm text-gray-500 line-clamp-2">
            {item.authors}
          </p>
        </div>
      </article>
    );
  }

  if (tab === "newsItems" && typeof item === "object") {
    return (
      <ul className="list-disc space-y-1 pl-4 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
        <li>
          <span className="font-semibold">{item.date}:</span> {item.title}
        </li>
      </ul>
    );
  }

  if (tab === "features" && typeof item === "object") {
    return (
      <ul className="list-disc space-y-1 pl-4 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
        <li>
          <span className="font-semibold">{item.date}:</span>{" "}
          <span dangerouslySetInnerHTML={{ __html: item.title || "" }} />
        </li>
      </ul>
    );
  }

  if (tab === "awardsAndHonours" && typeof item === "string") {
    return (
      <ul className="list-disc space-y-1 pl-4 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
        <li
          dangerouslySetInnerHTML={
            /<a\s/i.test(item) ? { __html: item } : undefined
          }
        >
          {/<a\s/i.test(item) ? null : item}
        </li>
      </ul>
    );
  }

  if (typeof item === "object") {
    return (
      <div className="rounded-lg border border-gray-200 p-3 text-sm dark:border-gray-700">
        <p className="font-semibold">
          {item.name || item.bigGoal || item.title}
        </p>
        {item.designation && (
          <p className="mt-1 text-sm text-gray-500">{item.designation}</p>
        )}
        {item.duration && (
          <p className="text-sm text-gray-500">{item.duration}</p>
        )}
        {item.description && (
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {item.description}
          </p>
        )}
      </div>
    );
  }

  return (
    <pre className="overflow-auto text-sm">{JSON.stringify(item, null, 2)}</pre>
  );
};

const HomeBioEditor = ({ draft, setDraft }) => {
  const pitch = draft.homePitch || {};

  return (
    <HomePitchEditor
      pitch={pitch}
      pillars={draft.researchPillars || []}
      onPitchChange={(next) =>
        setDraft((prev) => ({ ...prev, homePitch: next }))
      }
      note={
        <div className="rounded-lg border border-green/30 bg-green/5 px-3 py-2 text-sm text-gray-600 dark:text-gray-400">
          Theme <em>questions</em> come from the <strong>Pillars</strong> tab.
          Use <strong>Themes formatting</strong> below for bullets, numbers, or
          icons. Or edit on the live page via Visual Studio. Path story lives on{" "}
          <Link
            to="/pre-phd"
            className="text-green dark:text-green-dark underline underline-offset-2"
          >
            Background
          </Link>
          .
        </div>
      }
      footer={
        <p className="text-[11px] text-gray-500">
          Tip: after Save &amp; apply, open{" "}
          <Link
            to="/home"
            className="text-green dark:text-green-dark underline underline-offset-2"
          >
            /home
          </Link>{" "}
          to confirm. You can also use <strong>✎ Visual edit</strong> on Home.
        </p>
      }
    />
  );
};

const ItemEditor = ({
  tab,
  item,
  draft,
  onChange,
  onChangeTypes,
  extraFiles,
  onUploadFile,
}) => {
  if (item == null) return null;

  if (typeof item === "string") {
    return (
      <div>
        <TextArea
          label="Award text (HTML allowed)"
          value={item}
          rows={3}
          onChange={onChange}
        />
        <RichHint />
      </div>
    );
  }

  const set = (key, value) => onChange({ ...item, [key]: value });
  const setLink = (key, value) =>
    onChange({
      ...item,
      links: { ...(item.links || {}), [key]: value || undefined },
    });

  const pillarOptions = (draft.researchPillars || []).map((p) => ({
    id: p.id,
    label: p.bigGoal || p.title || p.id,
  }));
  const projectOptions = (draft.phdResearchProjects || []).map((p) => ({
    id: p.id,
    label: p.title || p.id,
  }));
  const collabOptions = (draft.collaborators || []).map((c) => ({
    id: c.id,
    label: c.firstname || c.name,
  }));
  const menteeOptions = (draft.mentoring || []).map((m) => ({
    id: m.id,
    label: m.firstname || m.name,
  }));

  const associationsBlock = (excludeTypeIds = []) => (
    <AssociationEditor
      item={item}
      draft={draft}
      onChangeItem={onChange}
      onChangeTypes={onChangeTypes}
      excludeTypeIds={excludeTypeIds}
    />
  );

  if (tab === "phdResearchProjects") {
    return (
      <div className="space-y-4">
        <SitePresence item={item} onToggleShown={(v) => set("shown", v)} />
        <div className="grid gap-3 sm:grid-cols-2">
          <TextInput
            label="ID (slug)"
            value={item.id}
            onChange={(v) => set("id", v)}
          />
          <TextInput
            label="Title"
            value={item.title}
            onChange={(v) => set("title", v)}
          />
          <TextInput
            label="Role"
            value={item.role}
            onChange={(v) => set("role", v)}
          />
          <TextInput
            label="Duration"
            value={item.duration}
            onChange={(v) => set("duration", v)}
            placeholder="Mar 2025 - Jun 2026"
          />
        </div>
        <TextArea
          label="Summary"
          value={item.summary}
          rows={4}
          onChange={(v) => set("summary", v)}
        />
        <ImagePicker value={item.img} onChange={(v) => set("img", v)} />
        <StringListEditor
          label="Contributions"
          values={item.contributions || []}
          onChange={(v) => set("contributions", v)}
        />
        <ChipMultiSelect
          label="Pillars"
          options={pillarOptions}
          values={item.pillarIds || []}
          onChange={(v) => set("pillarIds", v)}
        />
        <ChipMultiSelect
          label="Collaborators"
          options={collabOptions}
          values={item.collaboratorIds || []}
          onChange={(v) => set("collaboratorIds", v)}
        />
        <ChipMultiSelect
          label="Mentees"
          options={menteeOptions}
          values={item.menteeIds || []}
          onChange={(v) => set("menteeIds", v)}
        />
        <p className="text-[11px] text-gray-500">
          Missing someone? Add them under Collaborators / Mentoring first, then
          attach them here — or open their record and select this project.
        </p>
        {associationsBlock(["projects", "pillars", "collaborators", "mentees"])}
      </div>
    );
  }

  if (tab === "publications") {
    return (
      <div className="space-y-4">
        <TextArea
          label="Title"
          value={item.title}
          rows={2}
          onChange={(v) => set("title", v)}
        />
        <TextInput
          label="Authors"
          value={item.authors}
          onChange={(v) => set("authors", v)}
        />
        <div className="grid gap-3 sm:grid-cols-3">
          <TextInput
            label="Venue"
            value={item.venue}
            onChange={(v) => set("venue", v)}
          />
          <TextInput
            label="Year"
            type="number"
            value={item.year}
            onChange={(v) => set("year", v)}
          />
          <TextInput
            label="Month"
            type="number"
            value={item.month}
            onChange={(v) => set("month", v)}
          />
        </div>
        <TextInput
          label="Tags (comma-separated)"
          value={(item.tags || []).join(", ")}
          onChange={(v) =>
            set(
              "tags",
              v
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean),
            )
          }
        />
        <TextInput
          label="Keywords (comma-separated)"
          value={(item.keywords || []).join(", ")}
          onChange={(v) =>
            set(
              "keywords",
              v
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean),
            )
          }
        />
        <TextArea
          label="Comments"
          value={item.comments}
          rows={2}
          onChange={(v) => set("comments", v)}
        />
        <TextCheck
          label="Featured / selected"
          checked={item.selected}
          onChange={(v) => set("selected", v)}
        />
        <ImagePicker value={item.image} onChange={(v) => set("image", v)} />
        <ChipMultiSelect
          label="Pillars"
          options={pillarOptions}
          values={item.pillarIds || []}
          onChange={(v) => set("pillarIds", v)}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextInput
            label="DOI / publisher URL"
            value={item.links?.paper || ""}
            onChange={(v) => setLink("paper", v)}
          />
          <TextInput
            label="arXiv / preprint URL"
            value={item.links?.preprint || ""}
            onChange={(v) => setLink("preprint", v)}
          />
          <FilePicker
            label="PDF"
            value={item.links?.pdf}
            extraFiles={extraFiles}
            onChange={(v) => setLink("pdf", v)}
            onUpload={(file) =>
              onUploadFile(file, (path) => setLink("pdf", path))
            }
          />
          <FilePicker
            label="Poster"
            value={item.links?.poster}
            extraFiles={extraFiles}
            onChange={(v) => setLink("poster", v)}
            onUpload={(file) =>
              onUploadFile(file, (path) => setLink("poster", path))
            }
          />
          <FilePicker
            label="Slides"
            value={item.links?.slides}
            extraFiles={extraFiles}
            onChange={(v) => setLink("slides", v)}
            onUpload={(file) =>
              onUploadFile(file, (path) => setLink("slides", path))
            }
          />
        </div>
        {associationsBlock(["publications", "pillars"])}
      </div>
    );
  }

  if (tab === "researchPillars") {
    return (
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <TextInput
            label="ID"
            value={item.id}
            onChange={(v) => set("id", v)}
          />
          <TextInput
            label="Big goal"
            value={item.bigGoal}
            onChange={(v) => set("bigGoal", v)}
            placeholder="Reflection / Alignment / Agency"
          />
        </div>
        <TextArea
          label="Research question"
          value={item.title}
          rows={3}
          onChange={(v) => set("title", v)}
        />
        <TextArea
          label="Description"
          value={item.description}
          rows={3}
          onChange={(v) => set("description", v)}
        />
        <ChipMultiSelect
          label="Flagship projects (max 2 shown in modal)"
          options={projectOptions}
          values={item.flagshipProjectIds || []}
          onChange={(v) => set("flagshipProjectIds", v.slice(0, 2))}
        />
        {associationsBlock(["pillars"])}
      </div>
    );
  }

  if (tab === "newsItems") {
    return (
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <TextInput
            label="Date"
            value={item.date}
            onChange={(v) => set("date", v)}
          />
          <TextInput
            label="Type"
            value={item.type}
            onChange={(v) => set("type", v)}
          />
          <TextInput
            label="Category"
            value={item.category}
            onChange={(v) => set("category", v)}
          />
        </div>
        <TextArea
          label="Title"
          value={item.title}
          rows={2}
          onChange={(v) => set("title", v)}
        />
        <TextArea
          label="Description (HTML)"
          value={item.description}
          rows={4}
          onChange={(v) => set("description", v)}
        />
        <RichHint />
        <ChipMultiSelect
          label="Related pillars"
          options={pillarOptions}
          values={item.pillarIds || []}
          onChange={(v) => set("pillarIds", v)}
        />
        <ChipMultiSelect
          label="Related projects"
          options={projectOptions}
          values={item.projectIds || []}
          onChange={(v) => set("projectIds", v)}
        />
        {associationsBlock(["pillars", "projects"])}
      </div>
    );
  }

  if (tab === "features") {
    return (
      <div className="space-y-4">
        <TextInput
          label="Date"
          value={item.date}
          onChange={(v) => set("date", v)}
        />
        <TextArea
          label="Title / blurb (HTML)"
          value={item.title}
          rows={4}
          onChange={(v) => set("title", v)}
        />
        <RichHint />
        <ChipMultiSelect
          label="Related pillars"
          options={pillarOptions}
          values={item.pillarIds || []}
          onChange={(v) => set("pillarIds", v)}
        />
        <ChipMultiSelect
          label="Related projects"
          options={projectOptions}
          values={item.projectIds || []}
          onChange={(v) => set("projectIds", v)}
        />
        {associationsBlock(["pillars", "projects"])}
      </div>
    );
  }

  if (tab === "mentoring") {
    return (
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <TextInput
            label="ID"
            value={item.id}
            onChange={(v) => set("id", v)}
          />
          <TextInput
            label="First name (highlighted)"
            value={item.firstname}
            onChange={(v) => set("firstname", v)}
          />
          <TextInput
            label="Full name"
            value={item.name}
            onChange={(v) => set("name", v)}
          />
          <TextInput
            label="Duration"
            value={item.duration}
            onChange={(v) => set("duration", v)}
          />
        </div>
        <TextInput
          label="Designation"
          value={item.designation}
          onChange={(v) => set("designation", v)}
        />
        <ChipMultiSelect
          label="Projects"
          options={projectOptions}
          values={item.projectIds || []}
          onChange={(v) => set("projectIds", v)}
        />
        <p className="text-[11px] text-gray-500">
          Tip: linking here also updates each project&apos;s mentee list on
          sync.
        </p>
        {associationsBlock(["projects", "mentees"])}
      </div>
    );
  }

  if (tab === "collaborators") {
    return (
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <TextInput
            label="ID"
            value={item.id}
            onChange={(v) => set("id", v)}
          />
          <TextInput
            label="First name"
            value={item.firstname}
            onChange={(v) => set("firstname", v)}
          />
          <TextInput
            label="Full name"
            value={item.name}
            onChange={(v) => set("name", v)}
          />
          <TextInput
            label="Duration"
            value={item.duration}
            onChange={(v) => set("duration", v)}
          />
        </div>
        <TextInput
          label="Designation"
          value={item.designation}
          onChange={(v) => set("designation", v)}
        />
        <ChipMultiSelect
          label="Projects"
          options={projectOptions}
          values={item.projectIds || []}
          onChange={(v) => set("projectIds", v)}
        />
        <p className="text-[11px] text-gray-500">
          Tip: you can also assign people from a project&apos;s Collaborators /
          Mentees fields — links stay in sync both ways.
        </p>
        {associationsBlock(["projects", "collaborators"])}
      </div>
    );
  }

  if (tab === "associationTypes") {
    return (
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <TextInput
            label="ID (slug)"
            value={item.id}
            onChange={(v) => set("id", v)}
          />
          <TextInput
            label="Label"
            value={item.label}
            onChange={(v) => set("label", v)}
          />
        </div>
        <label className="block text-sm font-semibold uppercase text-gray-500">
          Kind
          <select
            className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-lightText dark:border-gray-600 dark:bg-gray-900 dark:text-darkText"
            value={item.kind || "collection"}
            onChange={(e) => set("kind", e.target.value)}
          >
            <option value="collection">Collection</option>
            <option value="urls">External links</option>
            <option value="text">Free text</option>
          </select>
        </label>
        {item.kind === "collection" && (
          <>
            <TextInput
              label="Target collection key"
              value={item.collection}
              onChange={(v) => set("collection", v)}
              placeholder="phdResearchProjects"
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <TextInput
                label="Option ID field"
                value={item.optionId}
                onChange={(v) => set("optionId", v)}
              />
              <TextInput
                label="Option label field"
                value={item.optionLabel}
                onChange={(v) => set("optionLabel", v)}
              />
            </div>
          </>
        )}
        <p className="text-[11px] text-gray-500">
          These types appear in the Associations panel on every item. You can
          also create types from that panel with “+ New association type”.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Field label="Raw JSON">
        <textarea
          className="mt-1 h-64 w-full rounded-md border border-gray-300 bg-white p-3 font-mono text-sm text-lightText dark:border-gray-600 dark:bg-gray-900 dark:text-darkText"
          value={JSON.stringify(item, null, 2)}
          onChange={(e) => {
            try {
              onChange(JSON.parse(e.target.value));
            } catch {
              /* ignore while typing */
            }
          }}
        />
      </Field>
      {associationsBlock()}
    </div>
  );
};

const Admin = () => {
  const { darkMode, setDarkMode } = useDarkMode();
  const [draft, setDraft] = useState(() => {
    const loaded = loadDraftFromStorage();
    return mergeDraftWithSite(loaded);
  });
  const [tab, setTab] = useState("phdResearchProjects");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [status, setStatus] = useState("");
  const [copied, setCopied] = useState(false);
  const [showJson, setShowJson] = useState(false);
  const [jsonText, setJsonText] = useState("");
  const [dragIndex, setDragIndex] = useState(null);
  const [extraFiles, setExtraFiles] = useState([]);
  const [apiOnline, setApiOnline] = useState(false);
  const [applying, setApplying] = useState(false);

  const items = Array.isArray(draft[tab]) ? draft[tab] : [];
  const selectedItem = items[selectedIndex];

  useEffect(() => {
    let cancelled = false;
    const ping = async () => {
      const ok = await checkContentApi();
      if (!cancelled) setApiOnline(ok);
    };
    ping();
    const id = setInterval(ping, 8000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  useEffect(() => {
    if (items[selectedIndex] !== undefined) {
      setJsonText(JSON.stringify(items[selectedIndex], null, 2));
    } else {
      setJsonText("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, selectedIndex]);

  const updateSelected = (next) => {
    setDraft((prev) => {
      const list = [...(prev[tab] || [])];
      list[selectedIndex] = next;
      let updated = { ...prev, [tab]: list };
      if (tab === "mentoring") {
        updated = syncProjectPeopleLinks(updated, {
          from: "person",
          role: "mentee",
          personId: next.id,
          projectIds: next.projectIds || [],
        });
      } else if (tab === "collaborators") {
        updated = syncProjectPeopleLinks(updated, {
          from: "person",
          role: "collaborator",
          personId: next.id,
          projectIds: next.projectIds || [],
        });
      } else if (tab === "phdResearchProjects") {
        updated = syncProjectPeopleLinks(updated, {
          from: "project",
          projectId: next.id,
          menteeIds: next.menteeIds || [],
          collaboratorIds: next.collaboratorIds || [],
        });
        updated = syncPillarContentLinks(updated, {
          from: "project",
          projectId: next.id,
          pillarIds: next.pillarIds || [],
        });
      } else if (tab === "researchPillars") {
        const prevItem = prev[tab]?.[selectedIndex];
        const assocChanged =
          JSON.stringify(prevItem?.associations || {}) !==
          JSON.stringify(next.associations || {});
        if (assocChanged) {
          updated = syncPillarContentLinks(updated, {
            from: "pillar",
            pillarId: next.id,
            associations: next.associations || {},
          });
        }
      } else if (tab === "publications") {
        updated = syncPillarContentLinks(updated, {
          from: "publication",
          title: next.title,
          pillarIds: next.pillarIds || [],
        });
      } else if (tab === "newsItems") {
        updated = syncPillarContentLinks(updated, {
          from: "news",
          title: next.title,
          pillarIds: next.pillarIds || [],
        });
      } else if (tab === "features") {
        updated = syncPillarContentLinks(updated, {
          from: "feature",
          title: next.title,
          pillarIds: next.pillarIds || [],
        });
      }
      return updated;
    });
    setJsonText(JSON.stringify(next, null, 2));
  };

  const addItem = () => {
    const blank = blankItem(tab);
    setDraft((prev) => ({ ...prev, [tab]: [...(prev[tab] || []), blank] }));
    setSelectedIndex(items.length);
    setStatus("Added new item.");
  };

  const deleteItem = () => {
    if (selectedItem === undefined) return;
    setDraft((prev) => ({
      ...prev,
      [tab]: (prev[tab] || []).filter((_, i) => i !== selectedIndex),
    }));
    setSelectedIndex(0);
    setStatus("Deleted item.");
  };

  const moveItem = (from, to) => {
    if (to < 0 || to >= items.length || from === to) return;
    setDraft((prev) => {
      const list = [...(prev[tab] || [])];
      const [moved] = list.splice(from, 1);
      list.splice(to, 0, moved);
      return { ...prev, [tab]: list };
    });
    setSelectedIndex(to);
  };

  const onDragStart = (index) => setDragIndex(index);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (index) => {
    if (dragIndex == null) return;
    moveItem(dragIndex, index);
    setDragIndex(null);
  };

  const saveDraft = () => {
    let synced = syncProjectPeopleLinks(draft);
    synced = syncPillarContentLinks(synced);
    setDraft(synced);
    saveDraftToStorage(synced);
    setStatus("Saved draft to localStorage (relations synced).");
  };

  const downloadDraft = () => {
    let synced = syncProjectPeopleLinks(draft);
    synced = syncPillarContentLinks(synced);
    setDraft(synced);
    saveDraftToStorage(synced);
    const blob = new Blob([JSON.stringify(synced, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "draft.json";
    a.click();
    URL.revokeObjectURL(url);
    setStatus("Downloaded draft.json — place it in content/ then run sync.");
  };

  const saveAndApply = async () => {
    let synced = syncProjectPeopleLinks(draft);
    synced = syncPillarContentLinks(synced);
    setDraft(synced);
    saveDraftToStorage(synced);
    if (!apiOnline) {
      setStatus(
        "Local content helper offline. Use Download draft.json + npm run content:sync, or restart with npm start.",
      );
      return;
    }
    setApplying(true);
    setStatus("Writing draft and syncing…");
    try {
      const result = await applyDraftViaApi(synced);
      setStatus(
        `${result.message}. Site data updated — wait for hot reload, then review git diff before commit.`,
      );
    } catch (err) {
      setStatus(`Apply failed: ${err.message}`);
      setApiOnline(false);
    } finally {
      setApplying(false);
    }
  };

  const resetToSite = () => {
    setDraft(buildSiteDraft());
    clearDraftFromStorage();
    setSelectedIndex(0);
    setStatus("Reset to current site data.");
  };

  const copySyncCommand = async () => {
    try {
      await navigator.clipboard.writeText("npm run content:sync");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setStatus("Run: npm run content:sync");
    }
  };

  const onUploadFile = async (file, assignPath) => {
    const safeName = file.name.replace(/\s+/g, "-");
    const path = `files/${safeName}`;

    if (apiOnline) {
      try {
        const result = await uploadFileViaApi(file);
        setExtraFiles((prev) => [...new Set([...prev, result.path || path])]);
        assignPath(result.path || path);
        setStatus(`${result.message}. Path saved on this item.`);
        return;
      } catch (err) {
        setStatus(
          `Upload via helper failed (${err.message}). Falling back to download…`,
        );
      }
    }

    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = url;
    a.download = safeName;
    a.click();
    URL.revokeObjectURL(url);
    setExtraFiles((prev) => [...new Set([...prev, path])]);
    assignPath(path);
    setStatus(
      `Prepared ${safeName}. Move the downloaded file into public/files/, then keep this path in the draft.`,
    );
  };

  const applyJson = () => {
    try {
      updateSelected(JSON.parse(jsonText));
      setStatus("Applied JSON edits.");
    } catch (err) {
      setStatus(`Invalid JSON: ${err.message}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-lightText dark:bg-darkBg dark:text-darkText">
      <div className="border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-6 py-4 sm:px-8">
          <div>
            <h1 className="text-xl font-semibold text-lightText dark:text-darkText">
              Portfolio Studio
            </h1>
            <p className="mt-0.5 text-sm text-gray-500">
              Visual content editor • helper{" "}
              <span
                className={
                  apiOnline
                    ? "text-green dark:text-green-dark"
                    : "text-amber-700"
                }
              >
                {apiOnline ? "online" : "offline"}
              </span>
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={saveAndApply}
              disabled={applying}
              className="rounded-md bg-green-solid px-3 py-1.5 text-sm text-white disabled:opacity-60"
              title={
                apiOnline
                  ? "Write content/draft.json and run content:sync"
                  : "Requires npm start (local content helper)"
              }
            >
              {applying ? "Applying…" : "Save & apply"}
            </button>
            <button
              type="button"
              onClick={saveDraft}
              className="rounded-md border border-green px-3 py-1.5 text-sm text-green dark:text-green-dark"
            >
              Save draft
            </button>
            <button
              type="button"
              onClick={downloadDraft}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-lightText dark:border-gray-600 dark:text-darkText"
            >
              Download draft.json
            </button>
            <button
              type="button"
              onClick={copySyncCommand}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-lightText dark:border-gray-600 dark:text-darkText"
            >
              {copied ? "Copied!" : "Copy sync"}
            </button>
            <button
              type="button"
              onClick={resetToSite}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-lightText dark:border-gray-600 dark:text-darkText"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={() => setDarkMode(!darkMode)}
              className="rounded-md border border-gray-300 p-2 text-lightText dark:border-gray-600 dark:text-darkText"
              aria-label={
                darkMode ? "Switch to light mode" : "Switch to dark mode"
              }
            >
              {darkMode ? <FaSun size={14} /> : <FaMoon size={14} />}
            </button>
            <Link
              to="/"
              className="rounded-md px-3 py-1.5 text-sm text-green dark:text-green-dark underline underline-offset-2"
            >
              ← Site
            </Link>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 py-5 sm:px-8">
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-gray-800 dark:text-amber-100">
          {apiOnline ? (
            <>
              <strong>One-click apply:</strong> use{" "}
              <strong>Save &amp; apply</strong> to write{" "}
              <code>content/draft.json</code> and sync into{" "}
              <code>src/data/</code> (no terminal). Then review the git diff and
              commit when ready. File uploads also write into{" "}
              <code>public/files/</code> when the helper is online.
            </>
          ) : (
            <>
              <strong>Manual publish:</strong> helper offline — edit here →
              Download <code>draft.json</code> into <code>content/</code> → run{" "}
              <code>npm run content:sync</code> → review git diff → commit.
              Restart with <code>npm start</code> to enable Save &amp; apply.
            </>
          )}
        </div>

        {status && (
          <p className="mb-3 rounded-md bg-green/10 px-3 py-2 text-sm text-green dark:text-green-dark">
            {status}
          </p>
        )}

        <div className="mb-4 flex flex-wrap gap-1.5">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => {
                setTab(t.id);
                setSelectedIndex(0);
                setShowJson(false);
              }}
              className={`rounded-full px-3 py-1.5 text-sm transition-colors ${
                tab === t.id
                  ? "bg-green-solid text-white"
                  : "bg-white text-gray-700 hover:bg-gray-100 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
              }`}
            >
              <span className="mr-1">{t.icon}</span>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "homeBio" ? (
          <div className="grid gap-4 xl:grid-cols-[minmax(0,1.15fr)_minmax(300px,0.85fr)]">
            <section className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900 sm:p-5">
              <h2 className="mb-4 text-base font-semibold">Edit • Home bio</h2>
              <HomeBioEditor draft={draft} setDraft={setDraft} />
            </section>
            <aside className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
              <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
                Live preview (Home about)
              </p>
              <HomeBio
                pitch={draft.homePitch || {}}
                pillars={draft.researchPillars}
                themeLinks={false}
              />
            </aside>
          </div>
        ) : (
          <div className="grid gap-4 xl:grid-cols-[280px_minmax(0,1fr)_280px]">
            {/* List */}
            <aside className="rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
              <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2 dark:border-gray-800">
                <span className="text-sm font-semibold uppercase text-gray-500">
                  {items.length} items
                </span>
                <button
                  type="button"
                  onClick={addItem}
                  className="text-sm font-medium text-green dark:text-green-dark underline underline-offset-2"
                >
                  + Add
                </button>
              </div>
              <ul className="max-h-[70vh] space-y-1 overflow-y-auto p-2">
                {items.map((item, index) => (
                  <li
                    key={index}
                    draggable={reorderableTabs.has(tab)}
                    onDragStart={() => onDragStart(index)}
                    onDragOver={onDragOver}
                    onDrop={() => onDrop(index)}
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedIndex(index)}
                      className={`flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm transition-colors ${
                        selectedIndex === index
                          ? "bg-green/10 text-green dark:text-green-dark"
                          : "hover:bg-gray-50 dark:hover:bg-gray-900"
                      }`}
                    >
                      {(tab === "phdResearchProjects" ||
                        tab === "publications") && (
                        <CardThumb
                          imageKey={
                            tab === "phdResearchProjects"
                              ? item.img
                              : item.image
                          }
                        />
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="line-clamp-2 font-medium">
                          {itemLabel(item, index)}
                        </span>
                        {tab === "phdResearchProjects" && item?.id ? (
                          <span className="mt-0.5 block text-[10px] text-gray-400">
                            {item.shown === false
                              ? "Hidden"
                              : siteProjects.some((p) => p.id === item.id)
                                ? isCurrentProject(item.duration)
                                  ? "Shown • Current"
                                  : "Shown • Past"
                                : "Shown • draft only"}
                            {reorderableTabs.has(tab)
                              ? " • drag to reorder"
                              : ""}
                          </span>
                        ) : (
                          reorderableTabs.has(tab) && (
                            <span className="mt-0.5 block text-[10px] text-gray-400">
                              Drag to reorder
                            </span>
                          )
                        )}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </aside>

            {/* Editor */}
            <section className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900 sm:p-5">
              {selectedItem === undefined ? (
                <p className="text-sm text-gray-500">No item selected.</p>
              ) : (
                <>
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                    <h2 className="text-base font-semibold">
                      Edit • {itemLabel(selectedItem, selectedIndex)}
                    </h2>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          moveItem(selectedIndex, selectedIndex - 1)
                        }
                        className="rounded border border-gray-300 px-2 py-1 text-sm text-lightText dark:border-gray-600 dark:text-darkText"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          moveItem(selectedIndex, selectedIndex + 1)
                        }
                        className="rounded border border-gray-300 px-2 py-1 text-sm text-lightText dark:border-gray-600 dark:text-darkText"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowJson((v) => !v)}
                        className="rounded border border-gray-300 px-2 py-1 text-sm text-lightText dark:border-gray-600 dark:text-darkText"
                      >
                        {showJson ? "Form view" : "JSON view"}
                      </button>
                      <button
                        type="button"
                        onClick={deleteItem}
                        className="rounded border border-red-300 px-2 py-1 text-sm text-red-600"
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  {showJson ? (
                    <div>
                      <textarea
                        value={jsonText}
                        onChange={(e) => setJsonText(e.target.value)}
                        className="h-96 w-full rounded-lg border border-gray-300 bg-gray-50 p-3 font-mono text-sm text-lightText dark:border-gray-600 dark:bg-gray-900 dark:text-darkText"
                        spellCheck={false}
                      />
                      <button
                        type="button"
                        onClick={applyJson}
                        className="mt-2 rounded bg-green-solid px-3 py-1.5 text-sm text-white"
                      >
                        Apply JSON
                      </button>
                    </div>
                  ) : (
                    <ItemEditor
                      tab={tab}
                      item={selectedItem}
                      draft={draft}
                      onChange={updateSelected}
                      onChangeTypes={(types) =>
                        setDraft((prev) => ({
                          ...prev,
                          associationTypes: types,
                        }))
                      }
                      extraFiles={extraFiles}
                      onUploadFile={onUploadFile}
                    />
                  )}
                </>
              )}
            </section>

            {/* Preview */}
            <aside className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
              <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
                Live preview
              </p>
              <LivePreview tab={tab} item={selectedItem} />
              {tab === "researchPillars" && draft.researchAgendaIntro && (
                <div className="mt-4 border-t border-gray-200 pt-3 dark:border-gray-800">
                  <p className="text-sm font-semibold text-gray-500">
                    Agenda intro
                  </p>
                  <p className="mt-1 text-sm font-medium">
                    {draft.researchAgendaIntro.title}
                  </p>
                  <p className="mt-1 text-[11px] leading-relaxed text-gray-500">
                    {draft.researchAgendaIntro.focus}
                  </p>
                </div>
              )}
            </aside>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
